import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import axiosInstance from '../axiosConfig';
import BeatLoader from "react-spinners/BeatLoader";
import ReactDOM from 'react-dom';
import PopupContent from './PopupContent';
import { useNavigate } from 'react-router-dom';

// Safe initialization with error checking
if (!process.env.REACT_APP_MAPBOX_API_KEY) {
  throw new Error('Missing Mapbox API key - check your environment variables');
}

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_API_KEY;

// Define constant outside useEffect
const initialCenter = [-98.5795, 39.8283];

// Helper: Convert courses array to GeoJSON FeatureCollection
const coursesToGeoJSON = (courses) => ({
    type: 'FeatureCollection',
    features: (courses || [])
        .filter(c => c.latitude && c.longitude && c.latitude !== 0 && c.longitude !== 0)
        .map(course => ({
            type: 'Feature',
            geometry: {
                type: 'Point',
                coordinates: [parseFloat(course.longitude), parseFloat(course.latitude)],
            },
            properties: {
                golf_course: course.golf_course || course.name || '',
                name: course.golf_course || course.name || '',
                city: course.city || '',
                state: course.state || '',
                initiation_fee: course.initiation_fee,
                slug: course.slug || '',
                club_id: course.club_id || course.id || '',
            },
        })),
});

const CourseMap = ({ courses, selectedCourse, selectedState, selectedCity }) => {
    const mapContainerRef = useRef(null);
    const mapRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const popupNodeRef = useRef(null);
    const coursesRef = useRef(courses); // Keep latest courses accessible in callbacks
    const mapLoadedRef = useRef(false);

    // Always keep coursesRef in sync
    coursesRef.current = courses;

    // Initialize map (runs once)
    useEffect(() => {
        const isMobile = window.innerWidth < 768;
        const mapInstance = new mapboxgl.Map({
            container: mapContainerRef.current,
            style: 'mapbox://styles/mapbox/light-v11',
            center: initialCenter,
            zoom: isMobile ? 2.5 : 3.5,
        });

        mapInstance.addControl(new mapboxgl.NavigationControl(), 'top-right');
        mapRef.current = mapInstance;

        const clickHandler = (e) => {
            const properties = e.features[0].properties;
            popupNodeRef.current = document.createElement('div');

            ReactDOM.render(
                <PopupContent properties={properties} navigate={navigate} />,
                popupNodeRef.current
            );

            new mapboxgl.Popup()
                .setLngLat(e.lngLat)
                .setDOMContent(popupNodeRef.current)
                .addTo(mapInstance);
        };

        mapInstance.on('load', () => {
            setLoading(false);
            mapLoadedRef.current = true;

            // GeoJSON source from database — uses coursesRef for latest data
            mapInstance.addSource('golf_courses', {
                type: 'geojson',
                data: coursesToGeoJSON(coursesRef.current),
            });

            mapInstance.addLayer({
                id: 'golf-course-layer',
                type: 'circle',
                source: 'golf_courses',
                paint: {
                    'circle-color': '#004d40',
                    'circle-radius': 6,
                    'circle-stroke-color': '#ffffff',
                    'circle-stroke-width': 2,
                },
            });

            mapInstance.on('click', 'golf-course-layer', clickHandler);

            // Pointer cursor on hover
            mapInstance.on('mouseenter', 'golf-course-layer', () => {
                mapInstance.getCanvas().style.cursor = 'pointer';
            });
            mapInstance.on('mouseleave', 'golf-course-layer', () => {
                mapInstance.getCanvas().style.cursor = '';
            });
        });

        return () => {
            if (popupNodeRef.current) {
                ReactDOM.unmountComponentAtNode(popupNodeRef.current);
            }
            mapInstance.remove();
        };
    }, [navigate]);

    // Update GeoJSON data when courses change (filtering is done in parent)
    useEffect(() => {
        if (mapRef.current && mapLoadedRef.current) {
            const source = mapRef.current.getSource('golf_courses');
            if (source) {
                source.setData(coursesToGeoJSON(courses));
            }
        }
    }, [courses]);

    // Zoom to state/city
    useEffect(() => {
        if (mapRef.current) {
            const isMobile = window.innerWidth < 768;
            const map = mapRef.current;

            const fetchAndFly = async (url, zoomLevel) => {
                try {
                    const response = await axiosInstance.get(url);
                    const { latitude, longitude } = response.data;
                    map.flyTo({
                        center: [longitude, latitude],
                        zoom: isMobile ? zoomLevel - 1 : zoomLevel,
                        offset: isMobile ? [0, -250] : [0, 0],
                    });
                } catch (err) {
                    console.error('Error fetching coordinates:', err);
                }
            };

            if (selectedCity) {
                fetchAndFly(`/city-coordinates/${selectedState}/${selectedCity}/`, 12);
            } else if (selectedState) {
                fetchAndFly(`/state-coordinates/${selectedState}/`, 6);
            } else {
                map.flyTo({
                    center: initialCenter,
                    zoom: isMobile ? 2.5 : 3.5,
                    offset: isMobile ? [0, -275] : [0, 0],
                });
            }
        }
    }, [selectedState, selectedCity]);

    // Fly to selected course
    useEffect(() => {
        if (mapRef.current && selectedCourse) {
            const isMobile = window.innerWidth < 768;
            const offset = isMobile ? [0, -200] : [0, 0];

            mapRef.current.flyTo({
                center: [selectedCourse.longitude, selectedCourse.latitude],
                zoom: 10,
                offset,
            });
        }
    }, [selectedCourse]);

    return (
        <div style={{ position: 'relative' }}>
            {loading && (
                <div className="map-loader">
                    <BeatLoader size={15} color={"#004d40"} />
                </div>
            )}
            <div ref={mapContainerRef} style={{ width: '100%', height: '100vh' }} />
        </div>
    );
};

export default CourseMap;
